import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { ChevronDown, ChevronRight, Plus, Wrench, CheckCircle, AlertCircle, Clock, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { businessToday } from '../utils/time';

const EQUIPMENT_TYPES = [
  { value: 'mower', label: 'Mower' },
  { value: 'trimmer', label: 'Trimmer' },
  { value: 'blower', label: 'Blower' },
  { value: 'edger', label: 'Edger' },
  { value: 'truck', label: 'Truck / Trailer' },
  { value: 'sprayer', label: 'Sprayer' },
  { value: 'chainsaw', label: 'Chainsaw' },
  { value: 'other', label: 'Other' },
];

function typeLabel(t: string) {
  return EQUIPMENT_TYPES.find(x => x.value === t)?.label || t;
}

function typeIcon(t: string) {
  const icons: Record<string, string> = {
    mower: '🚜', trimmer: '⚡', blower: '💨', edger: '🔧',
    truck: '🚛', sprayer: '💧', chainsaw: '🪚', other: '🔩',
  };
  return icons[t] || '🔩';
}

function statusBadge(overdue: number, dueSoon: number) {
  if (overdue > 0) return { label: `${overdue} overdue`, cls: 'bg-red-100 text-red-700' };
  if (dueSoon > 0) return { label: `${dueSoon} due soon`, cls: 'bg-amber-100 text-amber-700' };
  return { label: 'All OK', cls: 'bg-green-100 text-green-700' };
}

function taskStatus(nextDue: string | null) {
  if (!nextDue) return { label: 'No date', cls: 'text-gray-500', icon: Clock };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(nextDue + 'T00:00:00');
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, cls: 'text-red-600 font-semibold', icon: AlertCircle };
  if (diffDays === 0) return { label: 'Due today', cls: 'text-red-600 font-semibold', icon: AlertCircle };
  if (diffDays <= 14) return { label: `Due in ${diffDays}d`, cls: 'text-amber-600 font-semibold', icon: Clock };
  return { label: `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, cls: 'text-green-700', icon: CheckCircle };
}

const emptyEquip = { name: '', type: 'mower', make: '', model: '', serial_number: '', year: '', purchase_date: '', notes: '' };
const emptyTask = { task_name: '', interval_days: '30', last_done_at: '', notes: '' };

export default function EquipmentPage() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showAddEquip, setShowAddEquip] = useState(false);
  const [addTaskFor, setAddTaskFor] = useState<number | null>(null);
  const [logTaskId, setLogTaskId] = useState<{ taskId: number; taskName: string } | null>(null);
  const [logDate, setLogDate] = useState(businessToday());
  const [logNotes, setLogNotes] = useState('');
  const [equipForm, setEquipForm] = useState(emptyEquip);
  const [taskForm, setTaskForm] = useState(emptyTask);

  const { data, isLoading } = useQuery('equipment-list', () =>
    api.get('/equipment-mgmt').then(r => r.data.equipment as any[])
  );
  const equipment: any[] = data || [];

  const addEquip = useMutation(async (form: any) => {
    await api.post('/equipment-mgmt', form);
  }, {
    onSuccess: () => { qc.invalidateQueries('equipment-list'); setShowAddEquip(false); setEquipForm(emptyEquip); toast.success('Equipment added'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  const addTask = useMutation(async ({ equipId, form }: { equipId: number; form: any }) => {
    await api.post(`/equipment-mgmt/${equipId}/tasks`, form);
  }, {
    onSuccess: () => { qc.invalidateQueries('equipment-list'); qc.invalidateQueries('equipment-detail'); setAddTaskFor(null); setTaskForm(emptyTask); toast.success('Task added'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  const deleteTask = useMutation(async (taskId: number) => {
    await api.delete(`/equipment-mgmt/tasks/${taskId}`);
  }, {
    onSuccess: () => { qc.invalidateQueries('equipment-list'); qc.invalidateQueries('equipment-detail'); toast.success('Task removed'); },
    onError: () => { toast.error('Failed to delete'); },
  });

  const logDone = useMutation(async ({ taskId, done_at, notes }: { taskId: number; done_at: string; notes: string }) => {
    await api.post(`/equipment-mgmt/tasks/${taskId}/log`, { done_at, notes });
  }, {
    onSuccess: () => {
      qc.invalidateQueries('equipment-list');
      qc.invalidateQueries('equipment-detail');
      setLogTaskId(null); setLogDate(businessToday()); setLogNotes('');
      toast.success('Maintenance logged');
    },
    onError: () => { toast.error('Failed to log'); },
  });

  function toggle(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Overhead treats a tool with no price as free, so every mow has looked
  // cheaper than it is. Say how many, where it cannot be missed.
  const unpriced = equipment.filter((e: any) => Number(e.is_capital) === 1 && !(Number(e.purchase_price) > 0));

  return (
    <div className="space-y-6">
      {unpriced.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <b>{unpriced.length} tool{unpriced.length === 1 ? '' : 's'} with no price.</b> Overhead treats them as free, so
          every mow looks cheaper than it is. Open one and tap <b>Add price</b> — a rough figure and the month you
          bought it is enough.
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Equipment</h1>
        <button onClick={() => setShowAddEquip(true)} className="btn-primary text-sm">
          <Plus className="h-4 w-4 mr-1" /> Add Equipment
        </button>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">Loading…</div>
      ) : equipment.length === 0 ? (
        <div className="card p-10 text-center">
          <Wrench className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No equipment added yet</p>
          <p className="text-gray-500 text-sm mt-1">Add mowers, trimmers, and other equipment to track their maintenance.</p>
          <button onClick={() => setShowAddEquip(true)} className="btn-primary mt-4 text-sm">Add First Equipment</button>
        </div>
      ) : (
        <div className="space-y-3">
          {equipment.map((equip: any) => {
            const isOpen = expanded.has(equip.id);
            const badge = statusBadge(Number(equip.overdue_count), Number(equip.due_soon_count));
            return (
              <EquipCard
                key={equip.id}
                equip={equip}
                isOpen={isOpen}
                badge={badge}
                onToggle={() => toggle(equip.id)}
                onAddTask={() => { setAddTaskFor(equip.id); setTaskForm(emptyTask); }}
                onLogDone={(taskId, taskName) => { setLogTaskId({ taskId, taskName }); setLogDate(businessToday()); setLogNotes(''); }}
                onDeleteTask={(taskId) => { if (window.confirm('Remove this maintenance task?')) deleteTask.mutate(taskId); }}
              />
            );
          })}
        </div>
      )}

      {/* Add Equipment Modal */}
      {showAddEquip && (
        <Modal title="Add Equipment" onClose={() => setShowAddEquip(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label">Name *</label>
                <input className="input" placeholder="e.g. 48″ Husqvarna Mower" value={equipForm.name}
                  onChange={e => setEquipForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div>
                <label className="label">Type</label>
                <select className="input" value={equipForm.type} onChange={e => setEquipForm(f => ({ ...f, type: e.target.value }))}>
                  {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Year</label>
                <input className="input" type="number" placeholder="2022" value={equipForm.year}
                  onChange={e => setEquipForm(f => ({ ...f, year: e.target.value }))} />
              </div>
              <div>
                <label className="label">Make</label>
                <input className="input" placeholder="Husqvarna" value={equipForm.make}
                  onChange={e => setEquipForm(f => ({ ...f, make: e.target.value }))} />
              </div>
              <div>
                <label className="label">Model</label>
                <input className="input" placeholder="Z248F" value={equipForm.model}
                  onChange={e => setEquipForm(f => ({ ...f, model: e.target.value }))} />
              </div>
              <div>
                <label className="label">Serial #</label>
                <input className="input" value={equipForm.serial_number}
                  onChange={e => setEquipForm(f => ({ ...f, serial_number: e.target.value }))} />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input className="input" type="date" value={equipForm.purchase_date}
                  onChange={e => setEquipForm(f => ({ ...f, purchase_date: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows={2} value={equipForm.notes}
                  onChange={e => setEquipForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button className="btn-primary flex-1" disabled={addEquip.isLoading}
                onClick={() => addEquip.mutate(equipForm)}>
                {addEquip.isLoading ? 'Adding…' : 'Add Equipment'}
              </button>
              <button className="btn-secondary flex-1" onClick={() => setShowAddEquip(false)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Task Modal */}
      {addTaskFor !== null && (
        <Modal title="Add Maintenance Task" onClose={() => setAddTaskFor(null)}>
          <div className="space-y-3">
            <div>
              <label className="label">Task name *</label>
              <input className="input" placeholder="e.g. Oil change, Blade sharpening, Air filter" autoFocus
                value={taskForm.task_name} onChange={e => setTaskForm(f => ({ ...f, task_name: e.target.value }))} />
            </div>
            <div>
              <label className="label">Repeat every (days)</label>
              <input className="input" type="number" min={1} value={taskForm.interval_days}
                onChange={e => setTaskForm(f => ({ ...f, interval_days: e.target.value }))} />
              <p className="text-xs text-gray-500 mt-1">Common: oil change = 90 days, blade sharpen = 30 days, air filter = 60 days</p>
            </div>
            <div>
              <label className="label">Last done (optional)</label>
              <input className="input" type="date" value={taskForm.last_done_at}
                onChange={e => setTaskForm(f => ({ ...f, last_done_at: e.target.value }))} />
            </div>
            <div>
              <label className="label">Notes</label>
              <input className="input" placeholder="Any specific instructions…" value={taskForm.notes}
                onChange={e => setTaskForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2 pt-2">
              <button className="btn-primary flex-1" disabled={addTask.isLoading}
                onClick={() => addTask.mutate({ equipId: addTaskFor!, form: taskForm })}>
                {addTask.isLoading ? 'Adding…' : 'Add Task'}
              </button>
              <button className="btn-secondary flex-1" onClick={() => setAddTaskFor(null)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Log Done Modal */}
      {logTaskId && (
        <Modal title={`Log: ${logTaskId.taskName}`} onClose={() => setLogTaskId(null)}>
          <div className="space-y-3">
            <div>
              <label className="label">Date done</label>
              <input className="input" type="date" value={logDate}
                onChange={e => setLogDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Notes (optional)</label>
              <textarea className="input" rows={2} placeholder="Parts used, who did it, etc." value={logNotes}
                onChange={e => setLogNotes(e.target.value)} />
            </div>
            <div className="flex gap-2 pt-2">
              <button className="btn-primary flex-1" disabled={logDone.isLoading}
                onClick={() => logDone.mutate({ taskId: logTaskId!.taskId, done_at: logDate, notes: logNotes })}>
                {logDone.isLoading ? 'Saving…' : 'Mark Done'}
              </button>
              <button className="btn-secondary flex-1" onClick={() => setLogTaskId(null)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EquipCard({ equip, isOpen, badge, onToggle, onAddTask, onLogDone, onDeleteTask }: {
  equip: any; isOpen: boolean; badge: { label: string; cls: string };
  onToggle: () => void; onAddTask: () => void;
  onLogDone: (taskId: number, taskName: string) => void;
  onDeleteTask: (taskId: number) => void;
}) {
  const { data: detail } = useQuery(
    ['equipment-detail', equip.id],
    () => api.get(`/equipment-mgmt/${equip.id}`).then(r => r.data),
    { enabled: isOpen }
  );
  const tasks: any[] = detail?.tasks || [];
  const logs: any[] = detail?.logs || [];

  return (
    <div className="card overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center px-5 py-4 hover:bg-gray-50 text-left gap-3">
        <span className="text-2xl flex-shrink-0">{typeIcon(equip.type)}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">{equip.name}</p>
            <span className="text-xs text-gray-500">{typeLabel(equip.type)}</span>
            {equip.make && <span className="text-xs text-gray-500">{equip.make}{equip.model ? ` ${equip.model}` : ''}</span>}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
            {equip.task_count > 0 && <span className="text-xs text-gray-500">{equip.task_count} task{equip.task_count !== 1 ? 's' : ''}</span>}
            {Number(equip.is_capital) === 1 && !(Number(equip.purchase_price) > 0) && (
              <span className="text-xs font-medium text-amber-700">needs a price</span>
            )}
            {Number(equip.purchase_price) > 0 && Number(equip.life_months) > 0 && equip.purchase_date && (
              <span className="text-xs text-gray-500">${(Number(equip.purchase_price) / Number(equip.life_months)).toFixed(2)}/mo</span>
            )}
          </div>
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />}
      </button>

      {isOpen && (
        <div className="border-t border-gray-100">
          <CostEditor equip={equip} />
          {tasks.length === 0 ? (
            <p className="px-5 py-3 text-xs text-gray-500">No maintenance tasks yet.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {tasks.map((task: any) => {
                const st = taskStatus(task.next_due_date);
                const Icon = st.icon;
                return (
                  <li key={task.id} className="px-5 py-3 flex items-center gap-3">
                    <Icon className={`h-4 w-4 flex-shrink-0 ${st.cls}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{task.task_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Every {task.interval_days} days
                        {task.last_done_at ? ` · Last done ${new Date(task.last_done_at + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ' · Never done'}
                      </p>
                      {task.notes && <p className="text-xs text-gray-500 italic">{task.notes}</p>}
                    </div>
                    <span className={`text-xs font-medium ${st.cls} flex-shrink-0`}>{st.label}</span>
                    <button onClick={() => onLogDone(task.id, task.task_name)}
                      className="text-xs px-2.5 py-1 bg-brand-50 text-brand-700 rounded-lg font-medium hover:bg-brand-100 flex-shrink-0">
                      Done
                    </button>
                    <button onClick={() => onDeleteTask(task.id)}
                      className="text-gray-300 hover:text-red-500 flex-shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
            <button onClick={onAddTask}
              className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add task
            </button>
            {logs.length > 0 && (
              <details className="text-xs text-gray-500 cursor-pointer">
                <summary className="hover:text-gray-600">History ({logs.length})</summary>
                <ul className="mt-2 space-y-1 pl-2 border-l border-gray-200 max-h-40 overflow-y-auto">
                  {logs.map((log: any) => (
                    <li key={log.id} className="text-gray-500">
                      {new Date(log.done_at + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — {log.task_name}
                      {log.done_by_name ? ` (${log.done_by_name})` : ''}
                      {log.notes ? `: ${log.notes}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * What a tool cost and how long it lasts — the two numbers depreciation needs.
 *
 * Until now the only way to give a tool a price was to tell Claude. A tool with
 * no price is treated as free by the overhead engine, and a price with no
 * purchase date cannot be spread over months, so both are asked for here.
 */
function CostEditor({ equip }: { equip: any }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(equip.purchase_price ? String(equip.purchase_price) : '');
  const [bought, setBought] = useState(equip.purchase_date ? String(equip.purchase_date).slice(0, 10) : '');
  const [life, setLife] = useState(String(equip.life_months || 48));

  const save = useMutation(
    () => api.put(`/equipment-mgmt/${equip.id}/cost`, {
      purchase_price: Number(price), purchase_date: bought || null, life_months: Number(life) || 48,
    }),
    {
      onSuccess: (r: any) => {
        toast.success(r.data?.explains || 'Saved');
        qc.invalidateQueries('equipment-list');
        setEditing(false);
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
    });

  const field = 'w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm mt-1';
  const priced = Number(equip.purchase_price) > 0;
  const monthly = priced && Number(equip.life_months) > 0 ? Number(equip.purchase_price) / Number(equip.life_months) : null;

  if (!editing) {
    return (
      <div className="px-5 py-3 border-b border-gray-50 flex items-center gap-3 text-sm">
        {priced ? (
          <p className="flex-1 text-gray-700">
            ${Number(equip.purchase_price).toFixed(2)}
            {equip.purchase_date
              ? ` · bought ${new Date(String(equip.purchase_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
              : ' · no purchase date, so it is not being spread yet'}
            {monthly != null && equip.purchase_date && (
              <span className="text-gray-500"> · ${monthly.toFixed(2)} a month over {equip.life_months} months</span>
            )}
          </p>
        ) : (
          <p className="flex-1 text-amber-700">
            {Number(equip.is_capital) === 1 ? 'No price yet — overhead is treating this as free.' : 'No price recorded.'}
          </p>
        )}
        <button onClick={() => setEditing(true)}
          className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 font-medium flex-shrink-0">
          {priced ? 'Change' : 'Add price'}
        </button>
      </div>
    );
  }

  const preview = Number(price) > 0 && Number(life) > 0 ? Number(price) / Number(life) : null;
  return (
    <div className="px-5 py-3 border-b border-gray-50 space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs text-gray-500">Price
          <input type="number" min={0} step="0.01" value={price} onChange={e => setPrice(e.target.value)} className={field} placeholder="0.00" />
        </label>
        <label className="text-xs text-gray-500">Bought
          <input type="date" value={bought} onChange={e => setBought(e.target.value)} className={field} />
        </label>
        <label className="text-xs text-gray-500">Lasts (months)
          <input type="number" min={6} max={240} value={life} onChange={e => setLife(e.target.value)} className={field} />
        </label>
      </div>
      <p className="text-xs text-gray-500">
        {preview != null ? `$${preview.toFixed(2)} a month of overhead.` : 'Price and life give the monthly cost.'}
        {!bought && ' Without a purchase date it will not be spread.'}
      </p>
      <div className="flex justify-end gap-2">
        <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Cancel</button>
        <button onClick={() => save.mutate()} disabled={!(Number(price) > 0) || save.isLoading}
          className="press text-xs px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
          {save.isLoading ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
